import {
  createContext,
  type KeyboardEvent,
  type KeyboardEventHandler,
  type ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react'
import cx from 'classnames'
import { NEVER, type Observable } from 'rxjs'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { useObservableFocus } from '@/hooks/focus-hooks'
import styles from './ToggleGroup.module.css'

interface ToggleGroupProps {
  children?: ReactNode
  stretch?: boolean
  multi?: boolean
  focus$?: Observable<unknown>
  onFocus?: () => unknown
}

const ToggleGroupPropsContext = createContext<ToggleGroupProps>({})

/**
 * A group of toggle items, allowing one or several to be toggled on.
 */
export function ToggleGroup({
  children,
  multi,
  stretch,
  focus$ = NEVER,
  onFocus,
}: ToggleGroupProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const focusGroup = useCallback(
    () => (rootRef.current?.firstChild as HTMLElement)?.focus?.(),
    [],
  )
  useObservableFocus(focus$, focusGroup)
  return (
    <ToggleGroupPropsContext.Provider
      value={{ multi, stretch, focus$, onFocus }}
    >
      <div
        ref={rootRef}
        className={cx(styles.root, stretch && styles.stretched)}
        role={multi ? 'group' : 'radiogroup'}
      >
        {children}
      </div>
    </ToggleGroupPropsContext.Provider>
  )
}

interface ToggleItemProps {
  children?: ReactNode
  toggled?: boolean
  isDisabled?: boolean
  isNotValid?: boolean
  title?: string
  onKeyDown?: KeyboardEventHandler<unknown>
  onChange?: (newValue: boolean) => unknown
  onFocus?: () => unknown
  testId?: string
}

/**
 * A toggle item in a toggle group.
 */
export function ToggleItem({
  children,
  toggled,
  onChange,
  isDisabled,
  isNotValid,
  title,
  onKeyDown,
  testId,
  onFocus,
}: ToggleItemProps) {
  const { multi, onFocus: ctxOnFocus } = useContext(ToggleGroupPropsContext)
  const change = useCallback(() => {
    if (!isDisabled) {
      onChange?.(!toggled)
    }
  }, [toggled, isDisabled, onChange])
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      onKeyDown?.(e)
      if (e.defaultPrevented) {
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
      }
      if (e.key === ' ') {
        e.preventDefault()
        change()
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        ;(e.currentTarget.previousSibling as HTMLElement)?.focus?.()
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        ;(e.currentTarget.nextSibling as HTMLElement)?.focus?.()
      }
    },
    [change, onKeyDown],
  )

  const focus = useCallback(() => {
    onFocus?.()
    ctxOnFocus?.()
  }, [ctxOnFocus, onFocus])
  return (
    <RawButton
      role={multi ? 'checkbox' : 'radio'}
      onPress={change}
      title={title}
      aria-checked={!!toggled}
      onKeyDown={handleKeyDown}
      className={cx(
        styles.item,
        toggled && styles.toggled,
        isDisabled && styles.disabled,
        isNotValid && styles.isNotValid,
      )}
      testId={testId}
      onFocus={focus}
    >
      <div className={cx(styles.toggledIcon, toggled && styles.pop)}>
        <Icon
          auto
          className={cx(isDisabled && styles.disabledIcon)}
          name={
            multi
              ? toggled
                ? 'checkbox'
                : 'checkbox-blank'
              : toggled
                ? 'radio-selected'
                : 'radio'
          }
        />
      </div>
      <div className={styles.itemContent}>{children}</div>
    </RawButton>
  )
}
